import { HttpAgent } from '@ag-ui/client';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@coin/shared/auth/data-access';
import { environment } from '@coin/shared/core/util-environments';
import { CopilotKit } from '@copilotkitnext/angular';
import { first, from, switchMap } from 'rxjs';

@Injectable()
export class ChatStore {
  private readonly authService = inject(AuthService);
  private readonly copilotKit = inject(CopilotKit);
  private readonly overlay = inject(Overlay);
  private overlayRef: OverlayRef | null = null;

  private readonly _threadId = signal(crypto.randomUUID());
  readonly threadId = this._threadId.asReadonly();

  /** True once the chat has been opened at least once (triggers lazy runtime connection). */
  private readonly _chatOpenedOnce = signal(false);

  /** True after the first updateRuntime({ headers }) call. Subsequent refreshes bypass the signal chain. */
  private _headersInitialized = false;

  readonly isAuthenticated = toSignal(this.authService.isAuthenticated$);

  /** Sticky: once true, stays true — prevents copilot-chat destruction on token refresh flicker. */
  readonly everAuthenticated = toSignal(this.authService.isAuthenticated$.pipe(first(v => !!v)), {
    initialValue: false
  });

  private readonly idToken = toSignal(this.authService.idToken$, { initialValue: '' });
  readonly activeUser = toSignal(
    this.authService.isAuthenticated$.pipe(
      switchMap(isAuthenticated =>
        isAuthenticated ? from(this.authService.getActiveUser()) : from(Promise.resolve(undefined))
      )
    )
  );
  readonly user = computed(() => {
    const isAuthenticated = this.isAuthenticated();
    const user = this.activeUser();

    if (isAuthenticated && user) {
      return {
        gid: user.gid,
        firstName: user.given_name || user.firstName,
        lastName: user.family_name || user.lastName
      };
    }

    return null;
  });

  private readonly _isOpen = signal(false);
  readonly isOpen = this._isOpen.asReadonly();
  private _componentPortal: ComponentPortal<unknown> | null = null;

  constructor() {
    this.syncAuthHeaders();
    this.connectRuntimeLazily();
  }

  /**
   * Sync auth headers with CopilotKit.
   * IMPORTANT: CopilotKit's createAgentStoreSignal computed reads the #headers signal.
   * Calling updateRuntime({ headers }) updates that signal, which tears down the entire
   * AgentStore (and all messages) on every token refresh. To avoid this we only use
   * updateRuntime({ headers }) ONCE (before any AgentStore exists). For subsequent
   * token refreshes we silently patch the core's internal _headers and each agent's
   * headers property, bypassing the signal chain.
   * WARNING: This couples to @copilotkitnext/angular v1.52.x internals (_headers, core.agents).
   * If CopilotKit renames these internals, token refresh will silently break. Re-verify
   * after every CopilotKit version bump.
   */
  private syncAuthHeaders(): void {
    effect(() => {
      const token = this.idToken();
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

      if (!this._headersInitialized) {
        this._headersInitialized = true;
        this.copilotKit.updateRuntime({ headers });
      } else {
        // Token refresh: patch headers silently to avoid AgentStore teardown
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.copilotKit.core as any)._headers = headers;
        Object.values(this.copilotKit.core.agents).forEach(agent => {
          if (agent instanceof HttpAgent) {
            agent.headers = { ...headers };
          }
        });
      }
    });
  }

  /**
   * Lazy runtime connection: set runtimeUrl (which triggers the /info request) only
   * when the chat has been opened at least once AND a valid auth token is available.
   * This avoids the 401 that occurs on fresh page loads via auth callback where
   * provideCopilotKit() would fire /info before the token is ready.
   */
  private connectRuntimeLazily(): void {
    effect(() => {
      const chatOpened = this._chatOpenedOnce();
      const token = this.idToken();
      if (chatOpened && token) {
        this.copilotKit.updateRuntime({
          runtimeUrl: environment.chatBot.runtimeUrl
        });
      }
    });
  }

  setChatDialogComponent(componentPortal: ComponentPortal<unknown>): void {
    this._componentPortal = componentPortal;
  }

  setOpen(isOpen: boolean): void {
    this._isOpen.set(isOpen);
  }

  clearConversation(): void {
    this._threadId.set(crypto.randomUUID());
  }

  toggle(): void {
    if (!this._componentPortal) {
      console.error('No component portal was set');
      return;
    }

    const isOpen = this._isOpen();

    // Signal that the chat has been opened — triggers lazy CopilotKit runtime connection
    if (!isOpen) {
      this._chatOpenedOnce.set(true);
    }

    if (!this.overlayRef) {
      // First open: create overlay once and attach the component
      const positionStrategy = this.overlay.position().global().bottom('0.25rem').right('0.25rem').top('0.25rem');
      const config: OverlayConfig = {
        hasBackdrop: false,
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        panelClass: 'chatbot-overlay-panel'
      };
      this.overlayRef = this.overlay.create(config);
      this.overlayRef.attach(this._componentPortal);
    } else {
      // Subsequent toggles: hide/show without destroying the component so chat state is preserved
      this.overlayRef.overlayElement.style.display = isOpen ? 'none' : '';
    }

    this._isOpen.set(!isOpen);
  }
}
